"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { publicStorePath } from "@/lib/storefront/public-url";
import { identifyPosthogByEmail } from "@/lib/posthog-identify-email";
import { useHydrated } from "@/hooks/use-hydrated";
import { ContactFormNoScript } from "@/ui/components/contact-form-no-script";

const INQUIRY_TOPICS = ["kit", "order", "documentation", "returns", "partnership", "other"] as const;
type InquiryTopic = (typeof INQUIRY_TOPICS)[number];
type ApiContactTopic = "orders" | "returns" | "partnership" | "other";

function mapInquiryTopicToApi(topic: InquiryTopic): ApiContactTopic {
	if (topic === "order") return "orders";
	if (topic === "returns") return "returns";
	if (topic === "partnership") return "partnership";
	return "other";
}

interface InquiryFormProps {
	locale: string;
}

const inputClassName =
	"w-full rounded-lg border border-border bg-bg-2 px-3.5 py-2.5 text-sm text-foreground transition-colors placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring/30";

export function InquiryForm({ locale }: InquiryFormProps) {
	const t = useTranslations("footer.inquiry");
	const privacyHref = `${publicStorePath(locale, "/privacy")}`;
	const isHydrated = useHydrated();

	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<"formError" | "formErrorRateLimit" | null>(null);
	const [topic, setTopic] = useState<InquiryTopic>("kit");
	const tKit = useTranslations("kitWebsite");
	const identifyDedupeRef = useRef<string | null>(null);

	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!isHydrated || submitting) return;
		setSubmitting(true);
		setError(null);

		const form = e.currentTarget;
		const formData = new FormData(form);
		const name = String(formData.get("name") ?? "").trim();
		const email = String(formData.get("email") ?? "").trim();
		const company = String(formData.get("company") ?? "").trim();
		const phone = String(formData.get("phone") ?? "").trim();
		const message = String(formData.get("message") ?? "").trim();
		const consent = formData.get("consent") === "on";

		const topicLabel = tKit(
			`contact.topic${topic.charAt(0).toUpperCase()}${topic.slice(1)}` as Parameters<typeof tKit>[0],
		);
		const apiTopic = mapInquiryTopicToApi(topic);
		const fullMessage = `[${topicLabel}]\n\n${message}`;

		try {
			const res = await fetch("/api/contact", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					name,
					email,
					topic: apiTopic,
					message: fullMessage,
					consent,
					locale,
					company: company || undefined,
					phone: phone || undefined,
					contactSource: "footer_inquiry",
				}),
			});

			if (!res.ok) {
				const data = (await res.json()) as { error?: string };
				setError(data?.error === "too_many_requests" ? "formErrorRateLimit" : "formError");
				setSubmitting(false);
				return;
			}

			identifyPosthogByEmail(email, { name, contact_source: "footer_inquiry" }, identifyDedupeRef);
			setSubmitted(true);
		} catch {
			setError("formError");
		} finally {
			setSubmitting(false);
		}
	};

	if (submitted) {
		return (
			<div className="rounded-2xl border-2 border-primary bg-primary-soft p-8 text-center">
				<div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white">
					<svg
						viewBox="0 0 24 24"
						className="h-6 w-6"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden
					>
						<path d="M4 12 L10 18 L20 6" />
					</svg>
				</div>
				<h3 className="text-text font-display text-2xl font-medium tracking-[-0.012em]">
					{tKit("contact.success")}
				</h3>
				<button
					type="button"
					onClick={() => setSubmitted(false)}
					className="text-text-3 mt-6 font-mono text-[0.65rem] uppercase tracking-[0.18em] transition-colors hover:text-primary"
				>
					{t("formSuccessAgain")}
				</button>
			</div>
		);
	}

	return (
		<form
			method="post"
			onSubmit={(e) => void onSubmit(e)}
			className="flex h-full flex-col rounded-2xl border border-border bg-white p-6 lg:p-7"
		>
			<div className="mb-5 flex items-baseline justify-between border-b border-border pb-4">
				<div>
					<div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary">
						{t("formEyebrow")}
					</div>
					<h3 className="text-text mt-1 font-display text-[1.4rem] font-medium tracking-[-0.012em]">
						{t("formTitle")}
					</h3>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<Field label={t("formName")}>
					<input type="text" name="name" required autoComplete="name" className={inputClassName} />
				</Field>
				<Field label={t("formEmail")}>
					<input type="email" name="email" required autoComplete="email" className={inputClassName} />
				</Field>
				<Field label={t("formCompany")}>
					<input type="text" name="company" autoComplete="organization" className={inputClassName} />
				</Field>
				<Field label={t("formPhone")}>
					<input type="tel" name="phone" autoComplete="tel" className={inputClassName} />
				</Field>
			</div>

			<Field label={t("formTopic")} className="mt-4">
				<div className="flex flex-wrap gap-2">
					{INQUIRY_TOPICS.map((key) => (
						<button
							key={key}
							type="button"
							onClick={() => setTopic(key)}
							className={`rounded-full border px-3 py-1.5 font-mono text-[0.68rem] uppercase tracking-[0.14em] transition-colors ${
								topic === key
									? "border-primary bg-primary text-white"
									: "text-text-2 border-border bg-white hover:border-primary hover:text-primary"
							}`}
						>
							{tKit(
								`contact.topic${key.charAt(0).toUpperCase()}${key.slice(1)}` as Parameters<typeof tKit>[0],
							)}
						</button>
					))}
				</div>
			</Field>

			<Field label={t("formMessage")} className="mt-4">
				<textarea
					name="message"
					required
					rows={4}
					placeholder={tKit("contact.messagePlaceholder")}
					className={`${inputClassName} resize-none`}
				/>
			</Field>

			<label className="text-text-2 mt-4 flex items-start gap-2.5 text-[0.78rem] leading-snug">
				<input type="checkbox" name="consent" required className="mt-0.5 h-4 w-4 accent-primary" />
				<span>
					{t("formConsentPrefix")}
					<Link href={privacyHref} className="text-primary underline hover:text-primary-dark">
						{t("formConsentPrivacy")}
					</Link>
				</span>
			</label>

			{error ? (
				<p className="mt-3 text-sm text-destructive" role="alert">
					{t(error)}
				</p>
			) : null}
			<ContactFormNoScript />

			<div className="mt-6 flex flex-wrap items-center justify-end gap-3">
				<button
					type="submit"
					disabled={!isHydrated || submitting}
					className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-[0.7rem] font-medium uppercase tracking-[0.16em] text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
				>
					{submitting ? t("formSubmitting") : t("formSubmit")}
					{!submitting && <span aria-hidden>→</span>}
				</button>
			</div>
		</form>
	);
}

function Field({
	label,
	children,
	className = "",
}: {
	label: string;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<label className={`block ${className}`}>
			<span className="text-text-3 mb-1.5 block font-mono text-[0.62rem] uppercase tracking-[0.16em]">
				{label}
			</span>
			{children}
		</label>
	);
}
